import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    section: {
        marginTop: 10,
        padding: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 3,
    },
    sectionTitle: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 8,
        backgroundColor: '#f0f0f0',
        padding: 4,
        borderRadius: 2,
        textAlign: 'center',
    },
    subsectionTitle: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 4,
        marginTop: 6,
        color: '#495057',
        borderBottomWidth: 0.5,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
        paddingBottom: 2,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        marginBottom: 6,
    },
    gridItem: {
        width: '32%',
        marginBottom: 3,
        flexDirection: 'row',
        alignItems: 'center',
    },
    coloredGridItem: {
        width: '32%',
        marginBottom: 3,
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 2,
        padding: 2,
    },
    label: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#6c757d',
        marginRight: 4,
        minWidth: 60,
    },
    value: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#495057',
        flex: 1,
    },
    coloredLabel: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        marginRight: 4,
        minWidth: 60,
    },
    coloredValue: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        flex: 1,
    },
    greenBg: {
        backgroundColor: '#d4edda',
    },
    yellowBg: {
        backgroundColor: '#fff3cd',
    },
    redBg: {
        backgroundColor: '#f8d7da',
    },
    greenText: {
        color: '#155724',
    },
    yellowText: {
        color: '#856404',
    },
    redText: {
        color: '#721c24',
    },
    inlineRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        width: '100%',
    },
});

type BackgroundColorKey = 'greenBg' | 'yellowBg' | 'redBg';
type TextColorKey = 'greenText' | 'yellowText' | 'redText';

type ColorResult = {
    bg?: BackgroundColorKey;
    text?: TextColorKey;
    label?: TextColorKey;
};

const getOSAControlColor = (value: string | number | null | undefined): ColorResult => {
    if (value === null || value === undefined || value === '') {
        return {};
    }
    const numValue = parseFloat(String(value));
    if (Number.isNaN(numValue)) {
        return {};
    }

    if (numValue < 1) {
        return { bg: 'greenBg', text: 'greenText', label: 'greenText' };
    }
    if (numValue < 2) {
        return { bg: 'yellowBg', text: 'yellowText', label: 'yellowText' };
    }
    return { bg: 'redBg', text: 'redText', label: 'redText' };
};

const getCO2PurgeColor = (value: string | number | null | undefined): ColorResult => {
    if (value === null || value === undefined || value === '') {
        return {};
    }
    const numValue = parseFloat(String(value));
    if (Number.isNaN(numValue)) {
        return {};
    }

    if (numValue < 2501) {
        return { bg: 'greenBg', text: 'greenText', label: 'greenText' };
    }
    return { bg: 'redBg', text: 'redText', label: 'redText' };
};

interface InspectionSheetData {
    plenumSetPoint?: string | number;
    osaTemp?: string | number;
    vfdFan?: string | number;
    vfdFanUnit?: string;
    osaControl?: string | number;
    osaHumidity?: string | number;
    co2PurgeSp?: string | number;
    co2?: string | number;
    systemMode?: string;
    currentRunTime?: string | number;
    dailyAvg?: string | number;
    systemModeNotes?: string;
}

interface SystemInfoSectionProps {
    inspectionSheet: InspectionSheetData;
}

const SystemInfoSection: React.FC<SystemInfoSectionProps> = ({ inspectionSheet }) => {
    const osaControlColors = getOSAControlColor(inspectionSheet.osaControl);
    const co2PurgeColors = getCO2PurgeColor(inspectionSheet.co2PurgeSp);
    const formattedVfdFan = inspectionSheet.vfdFan
        ? `${inspectionSheet.vfdFan}${inspectionSheet.vfdFanUnit ? ` ${inspectionSheet.vfdFanUnit}` : ''}`
        : inspectionSheet.vfdFanUnit || '';
    const osaBackgroundStyle = osaControlColors.bg ? styles[osaControlColors.bg] : undefined;
    const osaLabelStyle = osaControlColors.label ? styles[osaControlColors.label] : undefined;
    const osaTextStyle = osaControlColors.text ? styles[osaControlColors.text] : undefined;
    const co2BackgroundStyle = co2PurgeColors.bg ? styles[co2PurgeColors.bg] : undefined;
    const co2LabelStyle = co2PurgeColors.label ? styles[co2PurgeColors.label] : undefined;
    const co2TextStyle = co2PurgeColors.text ? styles[co2PurgeColors.text] : undefined;
    const osaContainerStyles = osaBackgroundStyle
        ? [styles.coloredGridItem, osaBackgroundStyle]
        : [styles.coloredGridItem];
    const osaLabelStyles = osaLabelStyle
        ? [styles.coloredLabel, osaLabelStyle]
        : [styles.coloredLabel];
    const osaValueStyles = osaTextStyle
        ? [styles.coloredValue, osaTextStyle]
        : [styles.coloredValue];
    const co2ContainerStyles = co2BackgroundStyle
        ? [styles.coloredGridItem, co2BackgroundStyle]
        : [styles.coloredGridItem];
    const co2LabelStyles = co2LabelStyle
        ? [styles.coloredLabel, co2LabelStyle]
        : [styles.coloredLabel];
    const co2ValueStyles = co2TextStyle
        ? [styles.coloredValue, co2TextStyle]
        : [styles.coloredValue];

    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>System Information</Text>

            {/* Temperature Controls */}
            <Text style={styles.subsectionTitle}>Temperature & Controls</Text>
            <View style={styles.grid}>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>Plenum SP:</Text>
                    <Text style={styles.value}>{inspectionSheet.plenumSetPoint}°F</Text>
                </View>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>OSA Temp:</Text>
                    <Text style={styles.value}>{inspectionSheet.osaTemp}°F</Text>
                </View>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>VFD Fan:</Text>
                    <Text style={styles.value}>{formattedVfdFan}</Text>
                </View>
                <View style={osaContainerStyles}>
                    <Text style={osaLabelStyles}>OSA Control:</Text>
                    <Text style={osaValueStyles}>{inspectionSheet.osaControl}°F</Text>
                </View>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>OSA Humidity:</Text>
                    <Text style={styles.value}>{inspectionSheet.osaHumidity}%</Text>
                </View>
                <View style={styles.gridItem}>
                </View>
                <View style={co2ContainerStyles}>
                    <Text style={co2LabelStyles}>CO2 Purge SP:</Text>
                    <Text style={co2ValueStyles}>{inspectionSheet.co2PurgeSp}</Text>
                </View>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>CO2:</Text>
                    <Text style={styles.value}>{inspectionSheet.co2} ppm</Text>
                </View>
                <View style={styles.gridItem}>
                </View>
            </View>

            {/* Runtime Information */}
            <Text style={styles.subsectionTitle}>Runtime Data</Text>
            <View style={styles.grid}>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>System Mode:</Text>
                    <Text style={styles.value}>{inspectionSheet.systemMode}</Text>
                </View>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>Current Runtime:</Text>
                    <Text style={styles.value}>{inspectionSheet.currentRunTime} hrs</Text>
                </View>
                <View style={styles.gridItem}>
                    <Text style={styles.label}>Daily Average:</Text>
                    <Text style={styles.value}>{inspectionSheet.dailyAvg} hrs</Text>
                </View>
            </View>

            {/* System Mode Notes (inline to save space) */}
            <View style={styles.inlineRow}>
                <Text style={styles.label}>System Mode Notes:</Text>
                <Text style={[styles.value, { flex: 1 }]}>{inspectionSheet.systemModeNotes}</Text>
            </View>
        </View>
    );
};

export default SystemInfoSection;
